import React from 'react';
import { Card, Button, Select, Space, Divider, Alert } from 'antd';
import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import ConditionBuilder from './ConditionBuilder';
import { RulingConfigUtils, LOGICAL_OPERATORS } from '../../utils/rulingConfigUtils';

const { Option } = Select;

const RuleSetEditor = ({ 
  ruleSet, 
  onChange, 
  title, 
  color = 'blue',
  availableScorecards = ['scorecard_1', 'scorecard_2', 'scorecard_3', 'scorecard_4']
}) => {
  const handleConditionChange = (index, newCondition) => {
    const updatedConditions = [...(ruleSet.conditions || [])];
    updatedConditions[index] = newCondition;
    onChange({
      ...ruleSet,
      conditions: updatedConditions
    });
  };

  const handleConditionRemove = (index) => {
    const updatedConditions = (ruleSet.conditions || []).filter((_, i) => i !== index);
    onChange({
      ...ruleSet,
      conditions: updatedConditions
    });
  };

  const handleAddCondition = () => {
    const newCondition = RulingConfigUtils.createConditionTemplate();
    onChange({
      ...ruleSet,
      conditions: [...(ruleSet.conditions || []), newCondition]
    });
  };

  const handleLogicChange = (logic) => {
    onChange({
      ...ruleSet,
      logic
    });
  };

  const getLogicDescription = () => {
    if (!ruleSet.conditions || ruleSet.conditions.length <= 1) {
      return null;
    }

    if (ruleSet.logic === LOGICAL_OPERATORS.AND) {
      return "All conditions must be true for this rule to apply";
    } else if (ruleSet.logic === LOGICAL_OPERATORS.OR) {
      return "At least one condition must be true for this rule to apply";
    }
    
    return null;
  };

  const validateRuleSet = () => {
    return RulingConfigUtils.validateRuleSet(ruleSet, title);
  };

  const validation = validateRuleSet();

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined style={{ color }} />
          <span>{title} Rules</span>
        </div>
      }
      size="small"
      style={{
        border: `1px solid var(--border-secondary)`,
        backgroundColor: 'var(--bg-secondary)'
      }}
      headStyle={{
        backgroundColor: 'var(--bg-tertiary)',
        borderBottom: `1px solid var(--border-secondary)`
      }}
    >
      {/* Logic Selector */}
      {ruleSet.conditions && ruleSet.conditions.length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <Space align="center">
            <span style={{ fontSize: '13px', fontWeight: 500 }}>
              Logic:
            </span>
            <Select
              value={ruleSet.logic}
              onChange={handleLogicChange}
              size="small"
              style={{ width: 80 }}
            >
              <Option value={LOGICAL_OPERATORS.AND}>AND</Option>
              <Option value={LOGICAL_OPERATORS.OR}>OR</Option>
            </Select>
          </Space>
          
          {getLogicDescription() && (
            <div style={{ 
              marginTop: 4, 
              fontSize: '12px', 
              color: 'var(--text-secondary)', 
              fontStyle: 'italic' 
            }}>
              {getLogicDescription()}
            </div>
          )}
        </div>
      )}

      {/* Validation Alert */}
      {validation && (
        <Alert
          message="Rule Validation Error"
          description={validation}
          type="error"
          size="small"
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Conditions List */}
      <div style={{ marginBottom: 12 }}>
        {ruleSet.conditions && ruleSet.conditions.length > 0 ? (
          ruleSet.conditions.map((condition, index) => (
            <div key={index}>
              <ConditionBuilder
                condition={condition}
                onChange={(newCondition) => handleConditionChange(index, newCondition)}
                onRemove={() => handleConditionRemove(index)}
                showRemove={ruleSet.conditions.length > 1}
                availableScorecards={availableScorecards}
              />
              {index < ruleSet.conditions.length - 1 && (
                <Divider 
                  style={{ 
                    margin: '8px 0',
                    borderColor: 'var(--border-secondary)'
                  }}
                >
                  <span style={{ 
                    fontSize: '10px', 
                    color: 'var(--text-secondary)',
                    backgroundColor: 'var(--bg-secondary)',
                    padding: '0 8px'
                  }}>
                    {ruleSet.logic}
                  </span>
                </Divider>
              )}
            </div>
          ))
        ) : (
          <div style={{
            textAlign: 'center',
            padding: 24,
            color: 'var(--text-secondary)',
            fontSize: '13px'
          }}>
            No conditions defined. Click "Add Condition" to get started.
          </div>
        )}
      </div>

      {/* Add Condition Button */}
      <Button
        type="dashed"
        onClick={handleAddCondition}
        icon={<PlusOutlined />}
        size="small"
        style={{
          width: '100%',
          borderColor: 'var(--border-secondary)',
          color: 'var(--text-secondary)'
        }}
      >
        Add Condition
      </Button>

      {/* Rule Summary */}
      {ruleSet.conditions && ruleSet.conditions.length > 0 && (
        <div style={{
          marginTop: 12,
          padding: 8,
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: 4,
          fontSize: '12px',
          color: 'var(--text-secondary)'
        }}>
          <strong>Summary:</strong> {ruleSet.conditions.length} condition(s) 
          {ruleSet.conditions.length > 1 && ` with ${ruleSet.logic} logic`}
        </div>
      )}
    </Card>
  );
};

export default RuleSetEditor;